import { Box, Avatar, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../../../IdeationService';
import { formatTimestamp } from '../../../../SimulationXPLR/utils';

interface BotMessageProps {
  message: ChatMessage;
  sessionTitle?: string;
  onVersionSelect: (version: string, fileSummary: string) => void;
}

// ── Typing indicator shown while isSending is true ─────────────────────────────

export const TypingIndicator = () => (
  <Box sx={{ mb: 2, mr: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
    <BotAvatar size={32} />
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pt: 1 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#1A55F9',
            animation: 'blink 1.4s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes blink': {
              '0%, 60%, 100%': { opacity: 0.3 },
              '30%': { opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  </Box>
);

// ── Shared bot avatar ──────────────────────────────────────────────────────────
const BotAvatar = ({ size = 32 }: { size?: number }) => (
  <Avatar sx={{ width: size, height: size, backgroundColor: '#fff', fontSize: '14px' }}>
    <img src="/images/ai-xplr-logo-icon.svg" height={24} width={26} alt="xplr-logo" />
  </Avatar>
);

// ── Version / report box ───────────────────────────────────────────────────────
const VersionBox = ({
  label,
  versionTag,
  onClick,
}: {
  label: string;
  versionTag: string;
  onClick: () => void;
}) => (
  <Box
    sx={{
      width: '92%',
      backgroundColor: '#F1F1F1',
      borderRadius: '12px',
      p: '8px 16px',
      mt: 1,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      cursor: 'pointer',
    }}
    onClick={onClick}
  >
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        minWidth: 0,
        color: '#131313',
        fontSize: '16px',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      }}
    >
      {label}
    </Typography>

    <Typography
      variant="body2"
      sx={{
        color: '#757575',
        fontSize: '16px',
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      v{versionTag}
    </Typography>
  </Box>
);

// ── Markdown content styles (kept in one place for easy tweaking) ──────────────
const markdownSx = {
  color: '#131313',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: 1.5,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  '& p': { margin: '0 0 4px 0', lineHeight: 1.4 },
  '& ul, & ol': { paddingLeft: '18px', marginBottom: '4px' },
  '& li': { marginBottom: '2px', lineHeight: 1.5 },
  '& pre': { margin: '6px 0' },
  '& h1, & h2, & h3': { margin: '8px 0 4px', lineHeight: 1.4 },
};

// ── Main component ─────────────────────────────────────────────────────────────

const BotMessage = ({ message, sessionTitle, onVersionSelect }: BotMessageProps) => {
  // Whether to show the version / report box
  const hasVersionBox =
    ((message?.discoveryContent?.report && message?.version) || message?.fileSummary) &&
    message?.version !== '1.0.0';

  const versionTag = message?.version?.split('.')?.[2] ?? '';
  const versionLabel =
    sessionTitle ||
    (message?.discoveryContent?.report ? 'AI Ideation Report' : 'Attachment Summary');

  return (
    <Box sx={{ mb: 2, mr: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <BotAvatar />

      <Box sx={{ width: '90%' }}>
        {/* Markdown answer */}
        {message.answer && (
          <Box
            sx={{ width: '100%', borderRadius: '12px', px: 2, pt: 1, overflow: 'hidden', pl: 0 }}
          >
            <Typography component="div" variant="body2" sx={markdownSx}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.answer}</ReactMarkdown>
            </Typography>
          </Box>
        )}

        {/* Version box */}
        {hasVersionBox && (
          <VersionBox
            label={versionLabel}
            versionTag={versionTag}
            onClick={() => onVersionSelect(message?.version || '', message?.fileSummary || '')}
          />
        )}

        {/* Footer row: timestamp + time-taken */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: '#999', fontSize: '11px', display: 'block' }}>
            {formatTimestamp(message?.updated_at || '')}
          </Typography>

          {hasVersionBox && (message?.timeTaken ?? 0) > 0 && (
            <Typography
              variant="caption"
              sx={{ color: '#999', fontSize: '11px', mt: 0.5, display: 'block' }}
            >
              {`Time taken: ${String(message?.timeTaken ?? '0').substring(0, 4)}s`}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BotMessage;
